import React from "react"
import PropTypes from "prop-types"
import { useIntl } from "react-intl"
import { formatTimestamp, formatTooltipValue } from "../utils/formatters"

/**
 * CustomChartTooltip component
 * 
 * Custom tooltip for the historical exchange rate chart that displays
 * formatted timestamp and exchange rate value with locale-based formatting.
 * 
 * Requirements: 2.2, 5.4, 6.2, 6.3
 */
const CustomChartTooltip = ({ active, payload, quoteCurrency, timeRange }) => {
  const intl = useIntl()

  if (!active || !payload || !payload.length) {
    return null
  }

  const data = payload[0]
  const point = data.payload
  const timestamp = point.timestamp
  const value = point.value
  const change = typeof point.previousValue === "number" ? value - point.previousValue : null
  const changeColor = change === null ? "#666" : change >= 0 ? "#28a745" : "#dc3545"

  const formattedTimestamp = formatTimestamp(timestamp, timeRange, intl.locale)
  const formattedValue = formatTooltipValue(value, quoteCurrency, intl.locale)
  const formattedChange =
    change === null
      ? null
      : `${change >= 0 ? "+" : ""}${formatTooltipValue(change, quoteCurrency, intl.locale)}`

  return (
    <div
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        border: "1px solid #ccc",
        borderRadius: "4px",
        padding: "10px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
      }}
    >
      <p
        style={{
          margin: "0 0 5px 0",
          fontSize: "12px",
          color: "#666",
          fontWeight: "500",
        }}
      >
        {formattedTimestamp}
      </p>
      <p
        style={{
          margin: "0",
          fontSize: "14px",
          color: "#333",
          fontWeight: "bold",
        }}
      >
        {formattedValue}
      </p>
      {formattedChange && (
        <p
          style={{
            margin: "4px 0 0 0",
            fontSize: "12px",
            color: changeColor,
            fontWeight: "600",
          }}
        >
          {intl.formatMessage({ id: "Change" })}: {formattedChange}
        </p>
      )}
    </div>
  )
}

CustomChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      payload: PropTypes.shape({
        previousValue: PropTypes.number,
        timestamp: PropTypes.number,
        value: PropTypes.number,
      }),
      value: PropTypes.number,
    })
  ),
  quoteCurrency: PropTypes.string.isRequired,
  timeRange: PropTypes.string.isRequired,
}

export default CustomChartTooltip
